import React from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import Link from 'next/link';
import { ButtonStyleType } from '@/types';
import parse from 'html-react-parser';

interface CtaSmallProps {
  ctaStyle?: "bordered" | "background";
  title?: string;
  hasSubtitle?: boolean;
  subtitle?: string;
  buttonText?: string;
  buttonLink?: string; 
  buttonStyle?: ButtonStyleType;
  hasSecondaryButton?: boolean;
  alwaysStackButtons?: boolean;
  secondaryButtonText?: string;
  secondaryButtonLink?: string;
  secondaryButtonStyle?: ButtonStyleType;
  attributes?: React.HTMLProps<HTMLElement>;
  extraSpaceAtBottom?: boolean;
}

export const CtaSmall = ({ 
  ctaStyle,
  title,
  hasSubtitle,
  subtitle,
  buttonText,
  buttonLink,
  buttonStyle,
  hasSecondaryButton,
  alwaysStackButtons,
  secondaryButtonText,
  secondaryButtonLink,
  secondaryButtonStyle,
  attributes,
  extraSpaceAtBottom,
}: CtaSmallProps) => {
  const { key, ...rest } = attributes ?? {};
  const sectionClass = cn(
    'cta-small',
    extraSpaceAtBottom && 'mb-6',
  );
  const ctaOuterStyleClass = cn(
    "relative rounded-xl",
    ctaStyle === "bordered" && 'p-[1px] bg-image-gradient-primary',
    ctaStyle === "background" && 'bg-muted',
  );
  const ctaInnerStyleClass = cn(
    "p-6 flex flex-col gap-y-10",
    ctaStyle === "bordered" && 'rounded-[calc(theme(borderRadius.xl)-1px)] bg-muted',
    ctaStyle === "background" && 'rounded-xl bg-muted',
  );
  const buttonStackClass = cn(
    "flex w-full",
    alwaysStackButtons && 'flex-col gap-2',
    !alwaysStackButtons && 'flex-col md:flex-row gap-4 md:gap-2',
  );
  const buttonClass = cn(
    buttonText && buttonLink && !hasSecondaryButton && 'w-full md:w-auto md:flex-1',
    buttonText && buttonLink && hasSecondaryButton && !alwaysStackButtons && 'w-full md:w-auto md:flex-1/2',
  );

  return (
    <section
      key={key}
      {...rest}
      className={sectionClass}
    >
      <div className={ctaOuterStyleClass}>
        <div className={ctaInnerStyleClass}>
          <div className="flex flex-col gap-y-2">
            {title && (
              <>
              <h6>{parse(title)}</h6>
              {hasSubtitle && subtitle && (
                <span className="paragraph-base text-muted-foreground">{subtitle}</span>
              )}
              </>
            )}
          </div>
          {buttonText && buttonLink && (
            <div className={buttonStackClass}>
              <Button asChild variant={buttonStyle} size="sm" className={buttonClass}>
                <Link href={buttonLink || "/"} className="no-underline">{buttonText}</Link>
              </Button>
              {secondaryButtonText && secondaryButtonLink && (
                <Button asChild variant={secondaryButtonStyle} size="sm" className={buttonClass}>
                  <Link href={secondaryButtonLink || "/"} className="no-underline">{secondaryButtonText}</Link>
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}; 
